import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { PlusCircle, Search, ClipboardList, MoreHorizontal, Store, CreditCard, Briefcase, LogOut, MapPin, Loader2, ImagePlus, Clock4, AlertCircle, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useT } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { DISTRICTS, SHOP_TYPES, fmtDate } from "../lib/options";
import { ChoiceCard, Spinner, Empty, PageTitle, Chip } from "../components/Bits";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

function MoreMenu() {
  const { t } = useT();
  const { logout } = useAuth();
  const nav = useNavigate();
  const items = [[Briefcase, "my_jobs", "/employer/jobs"], [Store, "shop_profile", "/employer/setup"], [CreditCard, "plan_payment", "/employer/subscription"]];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="inline-flex min-h-[48px] items-center gap-2 rounded-xl border-2 border-slate-200 px-4 font-bold text-slate-700" data-testid="employer-more-menu-btn">
        <MoreHorizontal size={20} />{t("more")}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 rounded-2xl p-2">
        {items.map(([Icon, k, to]) => (
          <DropdownMenuItem key={k} onClick={() => nav(to)} className="min-h-[48px] gap-3 rounded-xl text-base font-semibold" data-testid={`more-menu-${k}`}><Icon size={20} />{t(k)}</DropdownMenuItem>
        ))}
        <DropdownMenuItem onClick={logout} className="min-h-[48px] gap-3 rounded-xl text-base font-semibold text-red-600" data-testid="more-menu-logout"><LogOut size={20} />{t("sign_out")}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PlanBanner({ s }) {
  const { t, lang } = useT();
  if (s.subscription) return <div className="rounded-2xl bg-emerald-50 p-4 font-semibold text-emerald-800" data-testid="plan-active-banner">{t("plan_active")} {fmtDate(s.subscription.ends_at, lang)}</div>;
  if (s.pending_payment) return <div className="flex items-start gap-3 rounded-2xl bg-amber-50 p-4 font-semibold text-amber-900" data-testid="plan-pending-banner"><Clock4 className="shrink-0" />{t("payment_pending")}</div>;
  return (
    <div className="flex flex-col gap-3 rounded-2xl border-2 border-[color:var(--orange)] bg-[color:var(--orange-soft)] p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="plan-none-banner">
      <div className="flex gap-3"><AlertCircle className="shrink-0 text-[color:var(--orange)]" /><div><p className="font-bold">{t("no_plan")}</p><p className="text-slate-700">{t("no_plan_sub")}</p></div></div>
      <Link to="/employer/subscription" className="btn-orange !min-h-[48px] shrink-0" data-testid="plan-choose-btn">{t("choose_plan")}</Link>
    </div>
  );
}

export function EmployerDashboard() {
  const { t } = useT();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/employer/summary").then((r) => setS(r.data)).catch((e) => { toast.error(errMsg(e)); setS({ org: null, error: true }); }); }, []);
  if (!s) return <Spinner />;
  if (!s.org && !s.error) return <Navigate to="/employer/setup" replace />;
  const cards = [
    { to: "/employer/jobs/new", icon: PlusCircle, label: t("post_job"), cls: "bg-[color:var(--orange)] text-white", id: "btn-post-job", sub: `${s.open_jobs ?? 0} ${t("open_count")}` },
    { to: "/employer/candidates", icon: Search, label: t("find_cands"), cls: "bg-[color:var(--navy)] text-white", id: "btn-find-candidates" },
    { to: "/employer/applications", icon: ClipboardList, label: t("review_apps"), cls: "bg-white text-[color:var(--navy)] border-2 border-[color:var(--navy)]", id: "btn-review-applications", sub: `${s.new_applications ?? 0} ${t("new_count")}` },
  ];
  return (
    <div className="page pb-12">
      <div className="mt-6 flex items-center justify-between gap-3">
        <div className="min-w-0"><p className="text-slate-600">{t("dash_hello")} {user.name?.split(" ")[0]}</p><h1 className="truncate text-2xl font-bold text-[color:var(--navy)] sm:text-3xl" data-testid="employer-org-name">{s.org?.name}</h1></div>
        <MoreMenu />
      </div>
      {s.org && <div className="mt-5"><PlanBanner s={s} /></div>}
      <div className="mt-6 grid gap-4 md:grid-cols-3">
        {cards.map(({ to, icon: Icon, label, cls, id, sub }) => (
          <Link key={id} to={to} data-testid={id} className={`flex min-h-[132px] flex-col justify-between rounded-3xl p-6 shadow-sm transition-transform hover:-translate-y-0.5 active:scale-[0.98] md:min-h-[200px] ${cls}`}>
            <Icon size={40} strokeWidth={2} />
            <div><p className="font-display text-2xl font-extrabold leading-tight">{label}</p>{sub && <p className="mt-1 font-semibold opacity-80">{sub}</p>}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function LocationsEditor({ value, onChange }) {
  const { t } = useT();
  const [n, setN] = useState({ name: "", district: "", address: "" });
  const add = () => { onChange([...(value || []), { ...n, address: n.address || null }]); setN({ name: "", district: "", address: "" }); };
  return (
    <div className="mt-6 space-y-3" data-testid="locations-editor">
      <h2 className="text-xl font-bold text-[color:var(--navy)]">{t("locations_t")}</h2>
      <p className="text-slate-600">{t("locations_sub")}</p>
      {(value || []).length === 0 ? <p className="text-sm text-slate-500">{t("no_locations")}</p> : (value || []).map((l, i) => (
        <div key={i} className="card-soft flex items-center gap-3 p-3" data-testid={`location-item-${i}`}>
          <MapPin className="shrink-0 text-[color:var(--orange)]" size={20} />
          <div className="min-w-0 flex-1"><p className="truncate font-bold">{l.name}</p><p className="truncate text-sm text-slate-600">{l.district}{l.address ? ` · ${l.address}` : ""}</p></div>
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="min-h-[44px] rounded-xl px-3 font-semibold text-red-600" data-testid={`location-remove-${i}`}>{t("remove")}</button>
        </div>
      ))}
      <div className="space-y-2 rounded-2xl bg-slate-50 p-3">
        <input className="field !min-h-[48px]" placeholder={t("loc_name")} value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} data-testid="location-name-input" />
        <select className="field !min-h-[48px]" value={n.district} onChange={(e) => setN({ ...n, district: e.target.value })} data-testid="location-district-select" aria-label={t("shop_district")}>
          <option value="">{t("shop_district")}</option>{DISTRICTS.map((d) => <option key={d}>{d}</option>)}
        </select>
        <input className="field !min-h-[48px]" placeholder={t("loc_address")} value={n.address} onChange={(e) => setN({ ...n, address: e.target.value })} data-testid="location-address-input" />
        <button type="button" className="btn-ghost !w-full" disabled={!n.name.trim() || !n.district} onClick={add} data-testid="location-add-btn">{t("add_location")}</button>
      </div>
    </div>
  );
}

export function EmployerSetup() {
  const { t } = useT();
  const nav = useNavigate();
  const fileRef = useRef();
  const [o, setO] = useState(null);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [isNew, setIsNew] = useState(true);
  useEffect(() => {
    let live = true;
    api.get("/employer/org").then((r) => {
      if (!live) return;
      setIsNew(!r.data);
      setO((prev) => prev || r.data || { name: "", shop_type: "", district: "", phone: "", description: "" });
    }).catch(() => live && setO((prev) => prev || { name: "" }));
    return () => { live = false; };
  }, []);
  if (!o) return <Spinner />;
  const set = (k, v) => setO((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/employer/org", { name: o.name, shop_type: o.shop_type, district: o.district, phone: o.phone, description: o.description, locations: o.locations || [] });
      toast.success(t("saved_ok"));
      nav("/employer");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const uploadLogo = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try { const { data } = await api.post("/employer/org/logo", fd); set("logo_url", data.logo_url); } catch (e) { toast.error(errMsg(e)); }
  };
  const steps = isNew ? 3 : 1;
  return (
    <div className="narrow pb-12 pt-2">
      <PageTitle>{t(isNew ? "setup_t" : "shop_profile")}</PageTitle>
      {(step === 1 || !isNew) && (
        <div className="space-y-3">
          <label className="label" htmlFor="org-name">{t("shop_name")}</label>
          <input id="org-name" className="field" value={o.name} onChange={(e) => set("name", e.target.value)} data-testid="org-name-input" />
          <label className="label pt-3" htmlFor="org-phone">{t("shop_phone")}</label>
          <input id="org-phone" className="field" type="tel" inputMode="tel" value={o.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="org-phone-input" />
          <p className="text-sm text-slate-500">{t("shop_phone_help")}</p>
        </div>
      )}
      {(step === 2 || !isNew) && (
        <div className="mt-4 space-y-3">
          <h2 className="text-xl font-bold text-[color:var(--navy)]">{t("shop_type")}</h2>
          <div className="grid grid-cols-2 gap-3">{SHOP_TYPES.map((v) => <ChoiceCard key={v} label={t(`t_${v}`)} selected={o.shop_type === v} onClick={() => set("shop_type", v)} testid={`org-type-${v}`} />)}</div>
        </div>
      )}
      {(step === 3 || !isNew) && (
        <div className="mt-4 space-y-3">
          <h2 className="text-xl font-bold text-[color:var(--navy)]">{t("shop_district")}</h2>
          <select className="field" value={o.district || ""} onChange={(e) => set("district", e.target.value)} data-testid="org-district-select">
            <option value="">—</option>{DISTRICTS.map((d) => <option key={d}>{d}</option>)}
          </select>
          <label className="label pt-3" htmlFor="org-desc">{t("shop_desc")}</label>
          <textarea id="org-desc" rows={3} className="field !py-3" value={o.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="org-desc-input" />
          <LocationsEditor value={o.locations} onChange={(v) => set("locations", v)} />
          {!isNew && (
            <div className="flex items-center gap-3 pt-2">
              {o.logo_url && <img src={o.logo_url} alt="" className="h-16 w-16 rounded-xl border object-cover" />}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files[0])} data-testid="org-logo-input" />
              <button className="btn-ghost !w-auto" onClick={() => fileRef.current.click()} data-testid="org-logo-btn"><ImagePlus size={20} />{t("add_logo")}</button>
            </div>
          )}
        </div>
      )}
      <div className="mt-8 flex gap-3">
        {isNew && step > 1 && <button className="btn-ghost !w-auto" onClick={() => setStep(step - 1)} data-testid="org-prev-btn">{t("back")}</button>}
        {isNew && step < steps ? (
          <button className="btn-orange flex-1" disabled={step === 1 ? !o.name.trim() || !(o.phone || "").trim() : !o.shop_type} onClick={() => setStep(step + 1)} data-testid="org-next-btn">{t("next")}</button>
        ) : (
          <button className="btn-orange flex-1" disabled={busy || !o.name.trim() || !o.district} onClick={save} data-testid="org-save-btn">{busy && <Loader2 className="animate-spin" />}{t(isNew ? "finish" : "save")}</button>
        )}
      </div>
    </div>
  );
}

export function MyJobs() {
  const { t, lang } = useT();
  const [jobs, setJobs] = useState(null);
  const [ask, setAsk] = useState(null);
  const load = () => api.get("/employer/jobs").then((r) => setJobs(r.data)).catch((e) => { toast.error(errMsg(e)); setJobs([]); });
  useEffect(() => { load(); }, []);
  const flip = async () => {
    try { await api.put(`/employer/jobs/${ask.id}`, { status: ask.status === "open" ? "closed" : "open" }); load(); } catch (e) { toast.error(errMsg(e)); }
    setAsk(null);
  };
  if (!jobs) return <Spinner />;
  return (
    <div className="narrow pb-12">
      <PageTitle>{t("my_jobs")}</PageTitle>
      <Link to="/employer/jobs/new" className="btn-orange !w-full" data-testid="myjobs-post-btn"><PlusCircle size={22} />{t("post_job")}</Link>
      <div className="mt-6 space-y-3">
        {jobs.length === 0 ? <Empty icon={Briefcase} title={t("no_my_jobs_t")} sub={t("no_my_jobs_sub")} testid="myjobs-empty" /> : jobs.map((j) => (
          <div key={j.id} className="card-soft p-4" data-testid={`myjob-${j.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div><p className="text-lg font-bold">{j.title}</p><p className="flex items-center gap-1 text-slate-600"><MapPin size={14} />{j.branch ? `${j.branch} · ` : ""}{j.location} · {fmtDate(j.created_at, lang)}</p></div>
              <Chip status={j.status === "open" ? "active" : "rejected"} label={t(j.status === "open" ? "job_open" : "job_closed")} />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link to={`/employer/applications?job_id=${j.id}`} className="inline-flex min-h-[48px] items-center rounded-xl bg-slate-100 px-4 font-bold" data-testid={`myjob-apps-${j.id}`}>{j.applications} {t("cands_count")}</Link>
              <button onClick={() => setAsk(j)} className="min-h-[48px] rounded-xl border-2 border-slate-200 px-4 font-bold" data-testid={`myjob-toggle-${j.id}`}>{t(j.status === "open" ? "close_job" : "reopen_job")}</button>
              <Link to={`/employer/jobs/${j.id}/edit`} className="inline-flex min-h-[48px] items-center gap-2 rounded-xl border-2 border-[color:var(--navy)] px-4 font-bold text-[color:var(--navy)]" data-testid={`myjob-edit-${j.id}`}><Pencil size={18} />{t("edit")}</Link>
            </div>
          </div>
        ))}
      </div>
      <AlertDialog open={!!ask} onOpenChange={() => setAsk(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader><AlertDialogTitle>{ask && t(ask.status === "open" ? "close_job" : "reopen_job")} ?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="min-h-[48px]" data-testid="myjob-toggle-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction className="min-h-[48px] bg-[color:var(--orange)]" onClick={flip} data-testid="myjob-toggle-confirm">{t("confirm_status")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
